import React, { useRef } from "react";

// useEffect
import { Box, Icon } from "@chakra-ui/react";
import { TIndividualGoalSavingsStatsItem } from "api/individual-savings/types";
import { ReactComponent as ArrowUp } from "assets/icons/arrow-down-red.svg";
import { ReactComponent as MoneyBag } from "assets/icons/money-bag-icon.svg";
import { ReactComponent as ProfileUsers } from "assets/icons/profile-two-users.svg";
import { ReactComponent as WalletMoney } from "assets/icons/wallet-money.svg";
import { useIndividualsSavings } from "contexts/individuals-savings-context";
// import { DEFAULT_SAVINGS_TRANSACTIONS_PAGINATION_PARAMS } from "features/individuals-savings/consts";
import { useElementWidth } from "hooks/useElementWidth";
// import { useSearchParams } from "react-router-dom";
import { handleAmountFormat } from "utils";
import { ONE, ONE_NAIRA_IN_KOBO, ZERO } from "utils/constants";
import { Maybe } from "utils/general/maybe";

import { Card } from "ui/card";
import { Spinner } from "ui/spinner";
import { Heading2, Paragraph4 } from "ui/typography";

import {
  individualStatsCardCss4,
  individualStatsCardInnerCss,
} from "../header/styles";
import {
  IIndividualsSavingsStats,
  IIndividualsSavingsStatsCardAssets,
  IIndividualsSavingsStatsProps,
} from "../header/types";

const individualStatsCardStyles: Record<
  number,
  IIndividualsSavingsStatsCardAssets
> = {
  0: {
    icon: <MoneyBag />,
    cardStyles: {
      iconBgColor: "#F1F1F1",
      iconBorder: "1px solid #DCDCDC",
    },
  },
  1: {
    icon: <ArrowUp />,
    cardStyles: {
      iconBgColor: "#FEF2F2",
      iconBorder: "1px solid #FCE4E4",
    },
  },
  2: {
    icon: <WalletMoney />,
    cardStyles: {
      iconBgColor: "#ECFDF3",
      iconBorder: "1px solid #D5F6E2",
    },
  },
  3: {
    icon: <ProfileUsers />,
    cardStyles: {
      iconBgColor: "#F1F1F1",
      iconBorder: "1px solid #DCDCDC",
    },
  },
};

const individualStatsKeyToLabelMap: Record<
  Exclude<TIndividualGoalSavingsStatsItem, "totalActiveGoals">,
  string
> = {
  totalAmountSaved: "Total Goal Savings Amount",
  totalWithdrawal: "Total Amount Withdrawn",
  totalInterestEarned: "Total Interest Earned",
  saversCount: "Total Number of Savers",
};

const DEFAULT_INDIVIDUAL_STATS: IIndividualsSavingsStats[] = [
  {
    title: individualStatsKeyToLabelMap.totalAmountSaved,
    value: "-",
  },
  {
    title: individualStatsKeyToLabelMap.totalWithdrawal,
    value: "-",
  },
  {
    title: individualStatsKeyToLabelMap.totalInterestEarned,
    value: "-",
  },
  {
    title: individualStatsKeyToLabelMap.saversCount,
    value: "-",
  },
];

const LG_WIDTH = 900;
export const IndividualsSavingsStats = (
  props: IIndividualsSavingsStatsProps
) => {
  const { isLoadingIndividualsStats } = props;
  // const [searchParams] = useSearchParams();
  // const page = searchParams.get("page");
  // const limit = searchParams.get("limit");
  const statsWrapper = useRef<HTMLDivElement | null>(null);
  const isLessThanLG = useElementWidth(statsWrapper, LG_WIDTH);
  const { individualGoalSavingsStats: individualsStatsData } =
    useIndividualsSavings();

  const individualsStats: IIndividualsSavingsStats[] = individualsStatsData
    ? Object.entries(individualsStatsData)
        .filter(([key]) => key !== "totalActiveGoals") // Exclude the unwanted key
        .map(([individualStatsKey, individualStatsValue], index, array) => {
          const isLastItem = index === array.length - ONE;

          return {
            title:
              individualStatsKeyToLabelMap[
                individualStatsKey as keyof typeof individualStatsKeyToLabelMap
              ],

            value: isLastItem
              ? Number(individualStatsValue).toLocaleString() // Just apply comma separation for the last item
              : handleAmountFormat({
                  value: String(individualStatsValue / ONE_NAIRA_IN_KOBO),
                  includeNairaSymbol: true,
                  shouldFormatZero: true,
                }) ||
                handleAmountFormat({
                  value: String(ZERO / ONE_NAIRA_IN_KOBO),
                  includeNairaSymbol: true,
                  shouldFormatZero: true,
                }),
          };
        })
    : DEFAULT_INDIVIDUAL_STATS;

  return (
    <div
      className={`h-[125px] mb-6 flex ${isLessThanLG ? "flex-wrap h-fit" : "items-center"} gap-6`}
      ref={statsWrapper}
    >
      {individualsStats.map(({ title, value }, idx) => {
        const { icon, cardStyles } =
          individualStatsCardStyles[idx] || individualStatsCardStyles[ZERO];

        return (
          <Card
            key={idx}
            header={
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                borderRadius="50%"
                width="36px"
                height="36px"
                bgColor={cardStyles?.iconBgColor}
                border={cardStyles?.iconBorder}
              >
                <Icon boxSize={"20px"}>{icon}</Icon>
              </Box>
            }
            body={
              <Box>
                <Paragraph4
                  lineHeight="22px"
                  textColor="#374151"
                >
                  {title}
                </Paragraph4>
                <Heading2
                  lineHeight="23px"
                  textColor="#1A202C"
                  className="tracking-[0.5px]"
                >
                  <Maybe condition={isLoadingIndividualsStats}>
                    <Spinner />
                  </Maybe>
                  <Maybe condition={!isLoadingIndividualsStats}>{value}</Maybe>
                </Heading2>
              </Box>
            }
            cardCss={individualStatsCardCss4}
            cardInnerCss={individualStatsCardInnerCss}
          />
        );
      })}
    </div>
  );
};
